import { observer } from 'mobx-react-lite'
import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router'

// evertel
import { InfoBox, Button, Input, Modal, ModalBody, Text, useUI, ModalHeader, ModalFooter } from '@evertel/web/ui'
import { RoomContext } from '../../RoomContext'
import { formatNumber } from '@evertel/utils'
import { useService } from '@evertel/di'
import { Analytics } from '@evertel/analytics'
import { CloneRoomController } from '@evertel/room'

interface CloneRoomModalProps {
    visible: boolean
    onClose: () => void
}


const CloneRoomModal: React.FC<CloneRoomModalProps> = observer(({
    visible,
    onClose
}) => {

    const { roomController } = useContext(RoomContext)
    const cloneRoomController = useService(CloneRoomController, [])
    const analytics = useService(Analytics, [])
    const navigate = useNavigate()
    const { addToast } = useUI()

    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [roomName, setRoomName] = useState('')
    const [roomDescription, setRoomDescription] = useState('')

    const toggleModal = () => {
        analytics.logView('clone-room', true)
        setRoomName('')
        setRoomDescription('')

        onClose()
    }

    const cloneRoom = async () => {

        if (!roomName?.trim()) {
            setError('Please give your room a name')
            return
        }

        setIsLoading(true)

        try {
            const clone = await cloneRoomController.cloneRoom(roomController.id, {
                name: roomName.trim(),
                description: roomDescription?.trim() || ''
            })

            addToast({
                color: 'success',
                message: 'This room has been cloned.'
            })

            setTimeout(() => navigate(`/room/${clone.id}`), 500)

            toggleModal()

        } catch (error) {
            setError(error.message)
        }

        setIsLoading(false)
    }

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            size="lg"
        >
            <ModalHeader
                title={`Clone "${roomController.room?.name}"`}
                subtitle="Cloning this room will copy all room settings and members to a new room"
            />
            <ModalBody className="mb-2">
                {(error) &&
                    <InfoBox
                        color="danger"
                        className="text-center">
                        {error}
                    </InfoBox>
                }
                {/* ROOM NAME */}
                <Text heavy>
                    Name Your Room
                </Text>
                <Input
                    name="roomName"
                    type="text"
                    value={roomName}
                    onChange={(e) => setRoomName(e.target.value)}
                    placeholder="Add a room name..."
                    className="mb-3"
                />
                {/* ROOM DESCRIPTION */}
                <div className="d-flex align-items-center">
                    <Text className="me-1" heavy>
                        Purpose/Description
                    </Text>
                    <small>(optional)</small>
                </div>
                <Input
                    name="roomDescrption"
                    type="text"
                    value={roomDescription}
                    onChange={(e) => setRoomDescription(e.target.value)}
                    placeholder="Add a short description..."
                />
                <InfoBox
                    color="info"
                    className="my-4 text-center">
                    <Text tag="h4">
                        <Text bold>{formatNumber(roomController.usersCount)}</Text><br/>
                        {`${(roomController.usersCount > 1) ? `people` : `person`}  will be added to this room`}
                    </Text>
                    <Text size="small">
                        (You can add more or remove people once the room is created)
                    </Text>
                </InfoBox>
            </ModalBody>
            <ModalFooter align="right">
                <Button
                    color="link"
                    outline
                    className="me-2"
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={cloneRoom}
                    loading={isLoading}
                >
                    Clone Room
                </Button>
            </ModalFooter>
        </Modal>
    )
})

export { CloneRoomModal }
